/**
 * Klantsessie op favotrip.be (Exclusive voor Belgische leden, fase 1,
 * Dennis 25-10-2026). Alleen publieke regels, zonder serverafhankelijkheden,
 * zodat de inlogpagina (client), de API-client en de BFF-route
 * (`src/app/api/klant/sessie/route.js`) precies dezelfde regel gebruiken.
 *
 * WAAROM. De backend zet `ft_customer_session` op Domain=.favotrip.nl; op
 * favotrip.be is die cookie onleesbaar. Op .be stuurt de backend na de magic
 * link of Google daarom door naar `https://www.favotrip.be/api/klant/sessie
 * ?code=<eenmalige code>&returnTo=<pad>`; die route wisselt de code
 * server-side in en zet dezelfde cookie op Domain=.favotrip.be.
 *
 * Achter `NEXT_PUBLIC_LUXE_BE` (standaard uit). Met de vlag uit, of op elke
 * andere host dan favotrip.be, is alles gelijk aan main: geen `portal`, en de
 * route geeft 404.
 *
 * Het inwisselgeheim staat bewust NIET in dit bestand (ook de naam niet): dit
 * bestand komt in de browserbundel. Alleen de route leest het.
 */
import { LUXE_BE_DOMEIN, exclusiveDomein, luxeBeAan } from "@/lib/luxe/luxe-markt";

/** Cookiedomein van de klantsessie op favotrip.be. */
export const KLANT_SESSIE_BE_DOMAIN = ".favotrip.be";

/** De portalwaarde die de backend kent voor favotrip.be. */
export const KLANT_PORTAL_BE = "be";

/** Standaard terugkeerpad, gelijk aan de gewone inlog. */
export const KLANT_DEFAULT_RETURN_TO = "/account/boekingen";

/** Het BFF-pad van de sessieroute (relatief, op de eigen .be-host). */
export const KLANT_SESSIE_PAD = "/api/klant/sessie";

/**
 * Het same-origin voorvoegsel voor de klant-API op .be
 * (`src/app/api/klant/api/[...pad]/route.js`): `/customer/me` wordt
 * `/api/klant/api/customer/me`, met de .be-cookie server-side doorgegeven.
 */
export const KLANT_API_PAD = "/api/klant/api";

/**
 * Staat de .be-klantsessie aan? Eén bron met de Exclusive-markt
 * (`luxeBeAan` in src/lib/luxe/luxe-markt.js, #1743): alleen precies "true".
 */
export function luxeBeEnabled() {
  return luxeBeAan();
}

/**
 * true voor favotrip.be, www.favotrip.be en staging.favotrip.be (met of zonder
 * poort). Zelfde domeinregel als luxe-markt.js; een lege of onbekende host is
 * daar favotrip.nl, dus hier false.
 */
export function isFavotripBeHost(hostname) {
  if (!hostname) return false;
  return exclusiveDomein(hostname) === LUXE_BE_DOMEIN;
}

/**
 * De `portal` die bij magic link en Google meegaat: "be" alleen met de vlag
 * aan en op een .be-host (of het site-context-domein "favotrip.be");
 * anders undefined (er gaat dan niets mee, zoals op main).
 */
export function klantPortalFor(hostOrDomain) {
  if (!luxeBeEnabled()) return undefined;
  return isFavotripBeHost(hostOrDomain) ? KLANT_PORTAL_BE : undefined;
}

/** Als `klantPortalFor`, voor de huidige browserhost (op de server: undefined). */
export function klantPortalInBrowser() {
  if (typeof window === "undefined") return undefined;
  try {
    return klantPortalFor(window.location.hostname);
  } catch {
    return undefined;
  }
}

// Exclusive/Luxe: zelfde regel als luxe-return-to.js. Account: /account of
// /account/<segment>[/<segment>...], alleen [A-Za-z0-9-] per segment. Geen
// query, geen fragment, geen `..`, geen `//`, geen backslash, geen domein.
const EXCLUSIVE_RETURN_TO = /^\/(?:exclusive|luxe)(?:\/[A-Za-z0-9-]*)?$/;
const ACCOUNT_RETURN_TO = /^\/account(?:\/[A-Za-z0-9-]+)*$/;
// Proefstand belevingen (fe#1751): zelfde regel als LUXE_PROEF_RETURN_TO in luxe-return-to.js.
const EXCLUSIVE_PROEF_RETURN_TO = /^\/exclusive\/[A-Za-z0-9-]+\?proef=belevingen$/;

/**
 * Een veilig relatief terugkeerpad na het inwisselen, of het standaardpad.
 * Een oud /luxe-pad wordt het overeenkomende /exclusive-pad.
 */
export function safeKlantReturnTo(returnTo) {
  if (typeof returnTo !== "string") return KLANT_DEFAULT_RETURN_TO;
  if (EXCLUSIVE_RETURN_TO.test(returnTo)) {
    return returnTo.startsWith("/luxe") ? `/exclusive${returnTo.slice("/luxe".length)}` : returnTo;
  }
  if (EXCLUSIVE_PROEF_RETURN_TO.test(returnTo)) return returnTo;
  if (ACCOUNT_RETURN_TO.test(returnTo)) return returnTo;
  return KLANT_DEFAULT_RETURN_TO;
}
